import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import MyLoader from "../Spinner/MyLoader";
import "../css/Admin_css/admin-customize.css";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import BilingualTextField from "../ChildComponent/BilingualTextField";
import MdlSelectSingleStudent from "../dialog/MdlSelectSingleStudent";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import {
  getRegionalFieldKey,
  getRegionalLanguageOption,
  REGIONAL_LANGUAGE_OPTIONS,
} from "../Utils/regionalTransliteration";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

function toInputDate(value) {
  if (!value) {
    return "";
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  //Created By Cursor — convert dd-MM-yyyy / dd/MM/yyyy to input[type=date]
  const match = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (match) {
    const day = match[1].padStart(2, "0");
    const month = match[2].padStart(2, "0");
    return `${match[3]}-${month}-${day}`;
  }
  return "";
}

//Created By Cursor — API/Android expect dd-MM-yyyy
function toApiDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-");
    return `${d}-${m}-${y}`;
  }
  return raw;
}

function formatClassName(classInfo) {
  if (!classInfo) {
    return "";
  }
  const className = classInfo.class_name || "";
  const sectionName = classInfo.section_name;
  if (sectionName && sectionName !== "null") {
    return className ? `${className}:${sectionName}` : sectionName;
  }
  return className;
}

//Created By Cursor — client fallback if API omits next_certificate_no
function nextCertificateNo(raw) {
  if (!raw) return "1";
  const match = String(raw).match(/^(.*?)(\d+)(\D*)$/);
  if (!match) return "1";
  const digits = match[2];
  let nextNum = String(parseInt(digits, 10) + 1);
  if (digits.length > nextNum.length) {
    nextNum = nextNum.padStart(digits.length, "0");
  }
  return `${match[1]}${nextNum}${match[3]}`;
}

const INITIAL_FORM = {
  certificateNo: "",
  admissionDate: "",
  passedDate: "",
  leavingDate: "",
  reasonForLeaving: "Parents Need",
  reasonForLeavingRegional: "",
  conduct: "Good",
  conductRegional: "",
  progress: "Good",
  progressRegional: "",
  language: "Hindi",
  languageRegional: "",
  remark: "Excellent",
  remarkRegional: "",
  studentName: "",
  studentNameRegional: "",
  fatherName: "",
  fatherNameRegional: "",
  board: "",
  boardRegional: "",
  medium: "",
  mediumRegional: "",
  className: "",
  classNameRegional: "",
  rollNumber: "",
  aadharNumber: "",
  gender: "",
  genderRegional: "",
  dob: "",
  religion: "",
  religionRegional: "",
  nationality: "",
  nationalityRegional: "",
  community: "",
  communityRegional: "",
  currentAddress: "",
  currentAddressRegional: "",
  permanentAddress: "",
  permanentAddressRegional: "",
};

function mapStudentDetailsToForm(student) {
  const classList = Array.isArray(student?.class) ? student.class : [];
  const classInfo = classList[0] || {};
  const today = new Date();
  const todayInput = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");

  //Created By Cursor — API resolves admission_date; fallback to first class registration_date
  const classRegistrationDate =
    classList
      .map((item) => item?.registration_date)
      .map((value) => toInputDate(value))
      .find((value) => !!value) || "";

  return {
    ...INITIAL_FORM,
    admissionDate:
      toInputDate(student?.admission_date) || classRegistrationDate || "",
    passedDate: todayInput,
    leavingDate: todayInput,
    studentName: student?.stu_name || "",
    fatherName: student?.father_name || "",
    board: classInfo.board_name || "",
    medium: classInfo.medium_name || "",
    className: formatClassName(classInfo),
    rollNumber: classInfo.student_roll_no || "",
    aadharNumber: student?.adharcard_no || "",
    gender: student?.gender || "",
    dob: toInputDate(student?.birthday),
    religion: student?.religion || "",
    nationality: student?.nationality || "",
    community: student?.community || "",
    currentAddress: student?.current_address || "",
    permanentAddress: student?.permanent_address || "",
  };
}

const PANEL_STYLE = {
  margin: "0 20px 20px",
  padding: "0",
};

const PAGE_TITLE_BAR_STYLE = {
  width: "100%",
  background: `linear-gradient(135deg, ${COLORS.primary_color_shade6} 0%, ${COLORS.primary_color_shade8} 100%)`,
  padding: "12px 18px",
  borderRadius: "8px 8px 0 0",
  boxShadow: `0 2px 4px ${COLORS.primary_color_shade3}`,
  marginTop: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "10px",
};

const PAGE_TITLE_STYLE = {
  fontSize: "20px",
  fontWeight: "700",
  color: "#fff",
  margin: 0,
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const PANEL_BODY_STYLE = {
  padding: "14px 16px 20px",
  background: COLORS.white,
};

const STYLES = {
  titleLanguageWrap: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },
  titleLanguageLabel: {
    margin: 0,
    fontSize: "10px",
    fontWeight: "800",
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    whiteSpace: "nowrap",
  },
  titleLanguageSelect: {
    minWidth: "170px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: "700",
    color: COLORS.primary_color_shade8,
    border: "2px solid rgba(255, 255, 255, 0.75)",
    borderRadius: "6px",
    outline: "none",
    background: "rgba(255, 255, 255, 0.95)",
    cursor: "pointer",
    boxShadow: "0 1px 4px rgba(1, 87, 155, 0.2)",
  },
  section: {
    marginBottom: "14px",
    borderRadius: "8px",
    border: `2px solid ${COLORS.primary_color_shade3}`,
    overflow: "hidden",
    boxShadow: `0 1px 4px ${COLORS.primary_color_shade2}55`,
  },
  sectionHeader: {
    margin: 0,
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: "800",
    color: COLORS.white,
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade4} 0%, ${COLORS.primary_color_shade6} 100%)`,
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  sectionBody: {
    padding: "10px 12px",
    background: COLORS.white,
  },
  bilingualWrap: {
    marginTop: "10px",
    borderTop: `2px solid ${COLORS.primary_color_shade2}`,
    paddingTop: "10px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    alignItems: "stretch",
  },
  bilingualPanel: {
    borderRadius: "8px",
    border: `2px solid ${COLORS.primary_color_shade3}`,
    overflow: "hidden",
    background: COLORS.white,
    minWidth: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  bilingualPanelHeader: {
    margin: 0,
    padding: "7px 10px",
    fontSize: "11px",
    fontWeight: "800",
    color: COLORS.primary_color_shade9,
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade1} 0%, ${COLORS.primary_color_shade2} 100%)`,
    borderBottom: `2px solid ${COLORS.primary_color_shade3}`,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  bilingualPanelBody: {
    padding: "8px",
    flex: 1,
  },
  bilingualGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "8px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "8px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  fieldFull: {
    gridColumn: "1 / -1",
  },
  label: {
    margin: 0,
    fontSize: "10px",
    fontWeight: "800",
    color: COLORS.primary_color_shade9,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    lineHeight: "1.2",
  },
  input: {
    width: "100%",
    padding: "7px 10px",
    fontSize: "12px",
    fontWeight: "700",
    color: COLORS.primary_color_shade9,
    border: `2px solid ${COLORS.primary_color_shade3}`,
    borderRadius: "6px",
    outline: "none",
    background: COLORS.white,
    transition: "all 0.2s ease",
    boxSizing: "border-box",
    lineHeight: "1.3",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    flexWrap: "wrap",
    marginTop: "4px",
  },
  submitBtn: {
    border: "none",
    borderRadius: "6px",
    padding: "8px 18px",
    fontSize: "12px",
    fontWeight: "800",
    color: COLORS.white,
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`,
    boxShadow: `0 2px 6px ${COLORS.primary_color_shade4}55`,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  cancelBtn: {
    border: `2px solid ${COLORS.primary_color_shade5}`,
    borderRadius: "6px",
    padding: "8px 18px",
    fontSize: "12px",
    fontWeight: "800",
    color: COLORS.primary_color_shade8,
    background: COLORS.white,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  studentSelectBtn: {
    width: "100%",
    padding: "12px 16px",
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`,
    border: "none",
    borderRadius: "8px",
    color: COLORS.white,
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    boxShadow: `0 2px 6px ${COLORS.primary_color_shade4}55`,
    transition: "all 0.2s ease",
  },
  studentCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 12px",
    marginBottom: "14px",
    borderRadius: "8px",
    border: `2px solid ${COLORS.primary_color_shade3}`,
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade0} 0%, ${COLORS.white} 100%)`,
    boxShadow: `0 1px 4px ${COLORS.primary_color_shade2}55`,
    flexWrap: "wrap",
  },
  studentPhoto: {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    objectFit: "cover",
    border: `2px solid ${COLORS.primary_color_shade4}`,
    background: COLORS.white,
    flexShrink: 0,
  },
  studentInfo: {
    flex: 1,
    minWidth: "180px",
  },
  studentName: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "800",
    color: COLORS.primary_color_shade9,
    lineHeight: 1.3,
  },
  studentMeta: {
    margin: "2px 0 0",
    fontSize: "11px",
    fontWeight: "700",
    color: COLORS.primary_color_shade7,
    lineHeight: 1.4,
  },
  changeStudentBtn: {
    border: `2px solid ${COLORS.primary_color_shade5}`,
    borderRadius: "6px",
    padding: "6px 12px",
    fontSize: "11px",
    fontWeight: "800",
    color: COLORS.primary_color_shade8,
    background: COLORS.white,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
};

const CERTIFICATE_FIELDS = [
  { key: "certificateNo", label: "Certificate Number", type: "text", placeholder: "Certificate Number" },
  { key: "admissionDate", label: "Admission Date", type: "date" },
  { key: "passedDate", label: "Passed Date", type: "date" },
  { key: "leavingDate", label: "Leaving Date", type: "date" },
  { key: "reasonForLeaving", label: "Reason For Leaving", type: "text", placeholder: "Parents Need", bilingual: true },
  { key: "conduct", label: "Conduct", type: "text", placeholder: "Good", bilingual: true },
  { key: "progress", label: "Progress", type: "text", placeholder: "Progress", bilingual: true },
  { key: "language", label: "Language", type: "text", placeholder: "Language", bilingual: true },
  { key: "remark", label: "Remark", type: "text", placeholder: "Remark", bilingual: true },
];

const STUDENT_FIELDS = [
  { key: "studentName", label: "Student Name", type: "text", placeholder: "Student name", bilingual: true },
  { key: "fatherName", label: "Father Name", type: "text", placeholder: "Father name", bilingual: true },
  { key: "board", label: "Board", type: "text", placeholder: "Board", bilingual: true },
  { key: "medium", label: "Medium", type: "text", placeholder: "Medium", bilingual: true },
  { key: "className", label: "Class", type: "text", placeholder: "Class", bilingual: true },
  { key: "rollNumber", label: "Roll Number", type: "text", placeholder: "Roll number" },
  { key: "aadharNumber", label: "Aadhar Number", type: "text", placeholder: "Aadhar number" },
  { key: "gender", label: "Gender", type: "text", placeholder: "Gender", bilingual: true },
  { key: "dob", label: "Date Of Birth", type: "date" },
  { key: "religion", label: "Religion", type: "text", placeholder: "Religion", bilingual: true },
  { key: "nationality", label: "Nationality", type: "text", placeholder: "Nationality", bilingual: true },
  { key: "community", label: "Community", type: "text", placeholder: "Community", bilingual: true },
  {
    key: "currentAddress",
    label: "Current Address",
    type: "text",
    placeholder: "Current address",
    bilingual: true,
    fullWidth: true,
    multiline: true,
  },
  {
    key: "permanentAddress",
    label: "Permanent Address",
    type: "text",
    placeholder: "Permanent address",
    bilingual: true,
    fullWidth: true,
    multiline: true,
  },
];

function PageHeader({ onBack }) {
  return (
    <div
      className="content-header"
      style={{ background: COLORS.primary_color_shade5 }}
    >
      <div className="leftside-content-header">
        <ul className="breadcrumbs">
          <li>
            <i
              className="fa fa-home"
              aria-hidden="true"
              style={{ color: COLORS.white }}
            />
            <span style={{ color: COLORS.white }}>Transfer Certificate</span>
          </li>
        </ul>
      </div>
      <div className="rightside-content-header">
        <BackButton onClick={onBack} />
      </div>
    </div>
  );
}

const FormField = memo(({ field, value, onChange }) => (
  <div
    style={{
      ...STYLES.field,
      ...(field.fullWidth ? STYLES.fieldFull : {}),
    }}
  >
    <label htmlFor={field.key} style={STYLES.label}>
      {field.label}
    </label>
    <input
      id={field.key}
      name={field.key}
      type={field.type}
      value={value == null ? "" : String(value)}
      placeholder={field.placeholder || ""}
      onChange={onChange}
      style={STYLES.input}
      onFocus={(e) => {
        e.target.style.borderColor = COLORS.primary_color_shade6;
        e.target.style.boxShadow = `0 0 0 2px ${COLORS.primary_color_shade2}`;
      }}
      onBlur={(e) => {
        e.target.style.borderColor = COLORS.primary_color_shade3;
        e.target.style.boxShadow = "none";
      }}
    />
  </div>
));

FormField.displayName = "FormField";

const RegionalLanguageSelector = memo(({ value, onChange }) => (
  <div style={STYLES.titleLanguageWrap}>
    <p style={STYLES.titleLanguageLabel}>
      <i className="fa fa-language" aria-hidden="true" />
      Regional
    </p>
    <select
      value={value}
      onChange={onChange}
      style={STYLES.titleLanguageSelect}
      aria-label="Regional language"
    >
      {REGIONAL_LANGUAGE_OPTIONS.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label} ({option.nativeLabel})
        </option>
      ))}
    </select>
  </div>
));

RegionalLanguageSelector.displayName = "RegionalLanguageSelector";

const StudentSelectionPanel = memo(
  ({ selectedStudent, onSelectStudent, onChangeStudent, editMode }) => {
  if (!selectedStudent) {
    return (
      <section style={{ marginBottom: "14px" }}>
        <button type="button" style={STYLES.studentSelectBtn} onClick={onSelectStudent}>
          <i className="fa fa-user-plus" aria-hidden="true" />
          Select Student
        </button>
      </section>
    );
  }

  const classList = Array.isArray(selectedStudent.class) ? selectedStudent.class : [];
  const classInfo = classList[0] || {};
  const classLabel = formatClassName(classInfo);
  const fatherLabel = selectedStudent.father_name
    ? `S/O: ${selectedStudent.father_name}`
    : "";
  const photoUrl = String(selectedStudent.student_img_path || "").trim();

  return (
    <section style={STYLES.studentCard}>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={selectedStudent.stu_name || "Student"}
          style={STYLES.studentPhoto}
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div
          style={{
            ...STYLES.studentPhoto,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: COLORS.primary_color_shade6,
          }}
        >
          <i className="fa fa-user" aria-hidden="true" />
        </div>
      )}
      <div style={STYLES.studentInfo}>
        <p style={STYLES.studentName}>{selectedStudent.stu_name || "Student"}</p>
        <p style={STYLES.studentMeta}>
          Enrollment: {selectedStudent.student_enrollment_no || "-"}
          {classLabel ? ` | Class: ${classLabel}` : ""}
        </p>
        {fatherLabel ? <p style={STYLES.studentMeta}>{fatherLabel}</p> : null}
      </div>
      {!editMode ? (
        <button type="button" style={STYLES.changeStudentBtn} onClick={onChangeStudent}>
          <i className="fa fa-refresh" aria-hidden="true" style={{ marginRight: "4px" }} />
          Change
        </button>
      ) : (
        <span
          style={{
            ...STYLES.changeStudentBtn,
            cursor: "default",
            opacity: 0.85,
          }}
        >
          <i className="fa fa-pencil" aria-hidden="true" style={{ marginRight: "4px" }} />
          Editing
        </span>
      )}
    </section>
  );
});

StudentSelectionPanel.displayName = "StudentSelectionPanel";

const FormSection = memo(
  ({ title, icon, fields, formData, regionalLanguage, onChange, onRegionalChange }) => {
    const standardFields = fields.filter((field) => !field.bilingual);
    const bilingualFields = fields.filter((field) => field.bilingual);
    const regionalOption = getRegionalLanguageOption(regionalLanguage);

    return (
      <section style={STYLES.section}>
        <h4 style={STYLES.sectionHeader}>
          <i className={`fa ${icon}`} aria-hidden="true" />
          {title}
        </h4>
        <div style={STYLES.sectionBody}>
          {standardFields.length > 0 ? (
            <div style={STYLES.grid}>
              {standardFields.map((field) => (
                <FormField
                  key={field.key}
                  field={field}
                  value={formData[field.key]}
                  onChange={(event) => onChange(field.key, event.target.value)}
                />
              ))}
            </div>
          ) : null}

          {bilingualFields.length > 0 ? (
            <div style={STYLES.bilingualWrap}>
              <div style={STYLES.bilingualPanel}>
                <p style={STYLES.bilingualPanelHeader}>
                  <i className="fa fa-font" aria-hidden="true" />
                  English
                </p>
                <div style={STYLES.bilingualPanelBody}>
                  <div style={STYLES.bilingualGrid}>
                    {bilingualFields.map((field) => (
                      <BilingualTextField
                        key={`${field.key}-en`}
                        mode="english"
                        fieldKey={field.key}
                        label={field.label}
                        englishValue={formData[field.key]}
                        regionalValue={formData[getRegionalFieldKey(field.key)]}
                        regionalLanguage={regionalLanguage}
                        placeholder={field.placeholder}
                        multiline={field.multiline}
                        fullWidth={field.fullWidth}
                        onEnglishChange={(value) => onChange(field.key, value)}
                        onRegionalChange={(value) =>
                          onRegionalChange(getRegionalFieldKey(field.key), value)
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div style={STYLES.bilingualPanel}>
                <p style={STYLES.bilingualPanelHeader}>
                  <i className="fa fa-language" aria-hidden="true" />
                  {regionalOption.label} ({regionalOption.nativeLabel})
                </p>
                <div style={STYLES.bilingualPanelBody}>
                  <div style={STYLES.bilingualGrid}>
                    {bilingualFields.map((field) => (
                      <BilingualTextField
                        key={`${field.key}-reg`}
                        mode="regional"
                        fieldKey={field.key}
                        label={field.label}
                        englishValue={formData[field.key]}
                        regionalValue={formData[getRegionalFieldKey(field.key)]}
                        regionalLanguage={regionalLanguage}
                        placeholder={field.placeholder}
                        multiline={field.multiline}
                        fullWidth={field.fullWidth}
                        onEnglishChange={(value) => onChange(field.key, value)}
                        onRegionalChange={(value) =>
                          onRegionalChange(getRegionalFieldKey(field.key), value)
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    );
  }
);

FormSection.displayName = "FormSection";

const TransferCertificateFormPage = memo(
  ({
    formData,
    regionalLanguage,
    loading,
    submitting,
    selectedStudent,
    editMode,
    onSelectStudent,
    onChangeStudent,
    onRegionalLanguageChange,
    onFieldChange,
    onRegionalFieldChange,
    onSubmit,
    onCancel,
    onBack,
  }) => (
    <div className="content" style={{ background: COLORS.white }}>
      <PageHeader onBack={onBack} />

      <div
        className="animated fadeInUp bg_page_white_rounded box_shadow"
        style={PANEL_STYLE}
      >
        <div style={PAGE_TITLE_BAR_STYLE}>
          <h3 style={PAGE_TITLE_STYLE}>
            <i
              className={`fa ${editMode ? "fa-pencil" : "fa-file-text-o"}`}
              aria-hidden="true"
              style={{ color: "#fff" }}
            />
            {editMode ? "Edit Leaving Certificate" : "Add Leaving Student"}
          </h3>
          <RegionalLanguageSelector
            value={regionalLanguage}
            onChange={onRegionalLanguageChange}
          />
        </div>

        <div style={PANEL_BODY_STYLE}>
          {loading ? (
            <MyLoader />
          ) : (
            <form onSubmit={onSubmit} noValidate>
              <StudentSelectionPanel
                selectedStudent={selectedStudent}
                onSelectStudent={onSelectStudent}
                onChangeStudent={onChangeStudent}
                editMode={editMode}
              />

              <FormSection
                title="Certificate Details"
                icon="fa-certificate"
                fields={CERTIFICATE_FIELDS}
                formData={formData}
                regionalLanguage={regionalLanguage}
                onChange={onFieldChange}
                onRegionalChange={onRegionalFieldChange}
              />

              <FormSection
                title="Student Details (As per admission time)"
                icon="fa-user"
                fields={STUDENT_FIELDS}
                formData={formData}
                regionalLanguage={regionalLanguage}
                onChange={onFieldChange}
                onRegionalChange={onRegionalFieldChange}
              />

              <div style={STYLES.actions}>
                <button
                  type="button"
                  style={STYLES.cancelBtn}
                  onClick={onCancel}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    ...STYLES.submitBtn,
                    opacity: submitting ? 0.7 : 1,
                    cursor: submitting ? "wait" : "pointer",
                  }}
                  disabled={submitting}
                >
                  <i
                    className="fa fa-check"
                    aria-hidden="true"
                    style={{ marginRight: "6px" }}
                  />
                  {submitting
                    ? editMode
                      ? "Updating…"
                      : "Submitting…"
                    : editMode
                    ? "Update"
                    : "Submit"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
);

TransferCertificateFormPage.displayName = "TransferCertificateFormPage";

const TransferCertificateForm = () => {
  const history = useHistory();
  const location = useLocation();
  const editCertificate = location.state?.mode === "edit" ? location.state?.certificate : null;
  const isEditMode = !!editCertificate;
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [regionalLanguage, setRegionalLanguage] = useState("hindi");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [originalCertificateNo, setOriginalCertificateNo] = useState("");
  const [isOpenStudentSelector, setIsOpenStudentSelector] = useState(false);

  useEffect(() => {
    if (!isEditMode) {
      setIsOpenStudentSelector(true);
    }
  }, [isEditMode]);

  //Created By Cursor — next certificate_no from tm_student_leaving_certificate
  const fetchNextCertificateNo = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      return "1";
    }

    try {
      const response = await axios.get(
        `${API_BASE()}getNextLeavingCertificateNo.php`,
        { params: { school_code: schoolCode } }
      );
      const rows = response.data?.data || [];
      if (response.data?.api_status === "OK" && rows.length) {
        return (
          String(rows[0].next_certificate_no || "").trim() ||
          nextCertificateNo(rows[0].certificate_no) ||
          "1"
        );
      }
    } catch (error) {
      console.error("Error fetching next certificate number:", error);
    }
    return "1";
  }, []);

  const fetchStudentDetails = useCallback(async (enrollmentNo, sessionId, certificateOverlay = null) => {
    setLoading(true);

    try {
      const requests = [
        axios.get(`${API_BASE()}searchStudentDetails.php`, {
          params: {
            school_code: SessionManager.getSchoolCode(),
            enrollment_no: enrollmentNo,
            session_id: sessionId,
            aadhar_no: "",
          },
        }),
      ];
      if (!certificateOverlay) {
        requests.push(fetchNextCertificateNo());
      }

      const results = await Promise.all(requests);
      const payload = results[0].data;
      const nextCertNo = certificateOverlay
        ? ""
        : results[1] || "1";

      if (payload.api_status === "OK" && payload.data?.length) {
        const student = payload.data[0];

        if (!Array.isArray(student.class) || !student.class.length) {
          // Edit can still work with certificate class fields
          if (!certificateOverlay) {
            myToast.notify_warning("Student not found in this session.");
            setSelectedStudent(null);
            setFormData(INITIAL_FORM);
            return;
          }
        }

        const mapped = mapStudentDetailsToForm(student);
        if (certificateOverlay) {
          const certClassId = certificateOverlay.passed_class_id || "";
          const certClassName = certificateOverlay.passed_class_name || "";
          if (certClassId || certClassName) {
            student.class = [
              {
                ...(student.class?.[0] || {}),
                class_id: certClassId || student.class?.[0]?.class_id,
                class_name: certClassName || student.class?.[0]?.class_name,
                session_id:
                  certificateOverlay.session_id ||
                  student.class?.[0]?.session_id ||
                  sessionId,
              },
            ];
          }
          setSelectedStudent(student);
          setSelectedSessionId(
            certificateOverlay.session_id ||
              sessionId ||
              student.class?.[0]?.session_id ||
              SessionManager.getCurrentSession()?.[0]?.session_id ||
              ""
          );
          setOriginalCertificateNo(
            String(certificateOverlay.certificate_no || "").trim()
          );
          setFormData({
            ...mapped,
            certificateNo: String(certificateOverlay.certificate_no || "").trim(),
            admissionDate: toInputDate(certificateOverlay.admission_date) || mapped.admissionDate,
            passedDate: toInputDate(certificateOverlay.passing_date) || mapped.passedDate,
            leavingDate: toInputDate(certificateOverlay.leaving_date) || mapped.leavingDate,
            reasonForLeaving:
              certificateOverlay.reason_for_leaving || mapped.reasonForLeaving,
            conduct: certificateOverlay.conduct || mapped.conduct,
            progress: certificateOverlay.progress || mapped.progress,
            language: certificateOverlay.language || mapped.language,
            remark: certificateOverlay.remark || mapped.remark,
            className: certClassName || mapped.className,
            studentName: certificateOverlay.stu_name || mapped.studentName,
            fatherName: certificateOverlay.father_name || mapped.fatherName,
          });
          myToast.notify_success("Certificate loaded for edit.");
          return;
        }

        setSelectedStudent(student);
        setSelectedSessionId(
          sessionId ||
            student.class?.[0]?.session_id ||
            SessionManager.getCurrentSession()?.[0]?.session_id ||
            ""
        );
        setFormData({
          ...mapped,
          certificateNo: nextCertNo || "1",
        });
        myToast.notify_success("Student details loaded.");
        return;
      }

      setSelectedStudent(null);
      setFormData(INITIAL_FORM);
      myToast.notify_warning(payload.api_remark || "No student found.");
    } catch (error) {
      console.error("Error fetching student details:", error);
      setSelectedStudent(null);
      setFormData(INITIAL_FORM);
      myToast.notify_warning("Failed to load student details.");
    } finally {
      setLoading(false);
    }
  }, [fetchNextCertificateNo]);

  //Created By Cursor — load certificate into form when editing
  const editLoadedRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || !editCertificate || editLoadedRef.current) {
      return;
    }
    const enrollmentNo = editCertificate.student_id || "";
    const sessionId =
      editCertificate.session_id ||
      SessionManager.getCurrentSession()?.[0]?.session_id ||
      "";
    if (!enrollmentNo || !sessionId) {
      myToast.notify_warning("Certificate details incomplete for edit.");
      return;
    }
    editLoadedRef.current = true;
    fetchStudentDetails(enrollmentNo, sessionId, editCertificate);
  }, [isEditMode, editCertificate, fetchStudentDetails]);

  const handleCloseStudentSelector = useCallback(() => {
    setIsOpenStudentSelector(false);
  }, []);

  const handleOpenStudentSelector = useCallback(() => {
    setIsOpenStudentSelector(true);
  }, []);

  const handleSelectedStudent = useCallback(
    (enrollmentNo, _selectedItem, sessionId) => {
      setIsOpenStudentSelector(false);
      setSelectedSessionId(sessionId || "");
      fetchStudentDetails(enrollmentNo, sessionId);
    },
    [fetchStudentDetails]
  );

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      history.push("/AdminMain/TransferCertificate");
    }
  }, [history]);

  const handleFieldChange = useCallback((fieldKey, value) => {
    setFormData((prev) => {
      if (prev[fieldKey] === value) {
        return prev;
      }
      return { ...prev, [fieldKey]: value };
    });
  }, []);

  const handleRegionalFieldChange = useCallback((fieldKey, value) => {
    setFormData((prev) => {
      if (prev[fieldKey] === value) {
        return prev;
      }
      return { ...prev, [fieldKey]: value };
    });
  }, []);

  const handleRegionalLanguageChange = useCallback((event) => {
    setRegionalLanguage(event.target.value);
  }, []);

  const handleCancel = useCallback(() => {
    history.push("/AdminMain/TransferCertificate");
  }, [history]);

  //Created By Cursor — issue leaving certificate via API (matches Android)
  const handleSubmit = useCallback(
    async (event) => {
      if (event?.preventDefault) {
        event.preventDefault();
      }
      if (event?.stopPropagation) {
        event.stopPropagation();
      }

      if (submittingRef.current) {
        return;
      }

      if (!selectedStudent) {
        myToast.notify_warning("Please select a student first.");
        return;
      }

      const certificateNo = String(formData.certificateNo || "").trim();
      if (!certificateNo) {
        myToast.notify_warning("Certificate number is required.");
        return;
      }

      if (!String(formData.admissionDate || "").trim()) {
        myToast.notify_warning("Admission date is required.");
        return;
      }
      if (!String(formData.passedDate || "").trim()) {
        myToast.notify_warning("Passed date is required.");
        return;
      }
      if (!String(formData.leavingDate || "").trim()) {
        myToast.notify_warning("Leaving date is required.");
        return;
      }

      const classInfo =
        (Array.isArray(selectedStudent.class) && selectedStudent.class[0]) ||
        {};
      const studentId =
        selectedStudent.student_enrollment_no ||
        selectedStudent.student_id ||
        editCertificate?.student_id ||
        "";
      const sessionId =
        selectedSessionId ||
        classInfo.session_id ||
        editCertificate?.session_id ||
        SessionManager.getCurrentSession()?.[0]?.session_id ||
        "";
      const staff = SessionManager.getStaffDetails() || {};
      const staffId = staff.staff_id || "";
      const schoolCode = SessionManager.getSchoolCode() || "";
      const classId =
        classInfo.class_id || editCertificate?.passed_class_id || "";
      const className =
        classInfo.class_name ||
        formData.className ||
        editCertificate?.passed_class_name ||
        "";

      if (!studentId) {
        myToast.notify_warning("Student id missing.");
        return;
      }
      if (!sessionId) {
        myToast.notify_warning("Session is missing. Select student again.");
        return;
      }
      if (!staffId) {
        myToast.notify_warning("Staff session required to issue certificate.");
        return;
      }
      if (!classId) {
        myToast.notify_warning("Class details missing for this student.");
        return;
      }

      const body = new URLSearchParams();
      body.set("certificate_no", certificateNo);
      body.set("issued_by", staffId);
      body.set("checked_by", staffId);
      body.set("student_id", studentId);
      body.set("admission_date", toApiDate(formData.admissionDate));
      body.set("passing_date", toApiDate(formData.passedDate));
      body.set("leaving_date", toApiDate(formData.leavingDate));
      body.set("passed_class_id", classId);
      body.set("passed_class_name", className);
      body.set(
        "reason_for_leaving",
        String(formData.reasonForLeaving || "").trim()
      );
      body.set("conduct", String(formData.conduct || "").trim());
      body.set("progress", String(formData.progress || "").trim());
      body.set("language", String(formData.language || "").trim());
      body.set("remark", String(formData.remark || "").trim());
      body.set("session_id", sessionId);
      body.set("school_code", schoolCode);

      const endpoint = isEditMode
        ? "updateLeavingCertificate.php"
        : "issueLeavingCertificate.php";
      if (isEditMode) {
        body.set(
          "original_certificate_no",
          originalCertificateNo || certificateNo
        );
      }

      submittingRef.current = true;
      setSubmitting(true);
      try {
        const res = await axios.post(
          `${API_BASE()}${endpoint}`,
          body.toString(),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (res.data?.api_status === "OK") {
          myToast.notify_success(
            isEditMode
              ? "Leaving certificate updated successfully."
              : "Leaving certificate issued successfully."
          );
          history.push("/AdminMain/TransferCertificate");
          return;
        }

        myToast.notify_warning(
          res.data?.api_remark ||
            (isEditMode
              ? "Failed to update leaving certificate."
              : "Failed to issue leaving certificate.")
        );
      } catch (error) {
        console.error(error);
        myToast.notify_error(
          error.response?.data?.api_remark ||
            error.message ||
            (isEditMode
              ? "Failed to update leaving certificate."
              : "Failed to issue leaving certificate.")
        );
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [
      formData,
      history,
      selectedSessionId,
      selectedStudent,
      isEditMode,
      editCertificate,
      originalCertificateNo,
    ]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <>
      <TransferCertificateFormPage
        formData={formData}
        regionalLanguage={regionalLanguage}
        loading={loading}
        submitting={submitting}
        selectedStudent={selectedStudent}
        editMode={isEditMode}
        onSelectStudent={handleOpenStudentSelector}
        onChangeStudent={handleOpenStudentSelector}
        onRegionalLanguageChange={handleRegionalLanguageChange}
        onFieldChange={handleFieldChange}
        onRegionalFieldChange={handleRegionalFieldChange}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onBack={handleBack}
      />

      {isOpenStudentSelector && !isEditMode ? (
        <MdlSelectSingleStudent
          action={handleCloseStudentSelector}
          action2={handleSelectedStudent}
        />
      ) : null}
    </>
  );
};

TransferCertificateForm.displayName = "TransferCertificateForm";

export default memo(TransferCertificateForm);
